import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";

import Span from "@vfuk/core-span";

import { Modal } from "@ucchub/ui";

import { Country } from "@components/country/Country.types";
import { CommercialCriteria } from "@components/customer/CommercialCriteria.types";
import useCreateEditCommercialCriteria from "@hooks/commercialCriteria/useCreateEditCommercialCriteria";

import AddCommercialCriteriaForm, {
  AddCommercialCriteriaFormValues,
} from "./AddCommercialCriteriaForm";
import AddCommercialCriteriaSuccessModal from "./AddCommercialCriteriaSuccessModal";
import commercialCriteriaFormValue from "./commercialCriteria.json";

interface AddCommercialCriteriaModalProps {
  isOpen: boolean;
  customerId: string;
  successModalModalOpen: boolean;
  setSuccessModalModalOpen: Dispatch<SetStateAction<boolean>>;
  countryDetails: Country[];
  commercialCriteriaList: CommercialCriteria[];
  onClose: () => void;
}

const AddCommercialCriteriaModal = ({
  isOpen,
  customerId,
  successModalModalOpen,
  setSuccessModalModalOpen,
  countryDetails,
  commercialCriteriaList,
  onClose,
}: AddCommercialCriteriaModalProps) => {
  const { t } = useTranslation();

  const [formValues, setFormValues] = useState<AddCommercialCriteriaFormValues>(
    JSON.parse(JSON.stringify(commercialCriteriaFormValue))
  );

  const [activeStep, setActiveStep] = useState(0);
  const totalSteps = 3;

  const handleActiveStep = (step: number) => {
    if (step < 0 || step > totalSteps) {
      return;
    }
    setActiveStep(step);
  };
  const onDiscard = () => {
    // Reset all the values
    setSuccessModalModalOpen(false);
  };

  const { submitCreateCommercialCriteria, error, setError } =
    useCreateEditCommercialCriteria();

  const closeModal = () => {
    handleActiveStep(0);
    setFormValues(JSON.parse(JSON.stringify(commercialCriteriaFormValue)));
    setError("");
    onClose();
  };

  const handleOnSubmit = () => {
    const { isCheckboxChecked, ...formData } = formValues;

    if (!isCheckboxChecked) return;

    submitCreateCommercialCriteria({ orgId: customerId, ...formData })
      .then(() => {
        closeModal();
        setSuccessModalModalOpen(true);
      })
      .catch(() => {
        handleActiveStep(0);
      });
  };

  const newCommercialCriteriaName =
    commercialCriteriaList[commercialCriteriaList.length - 1]?.name;

  return (
    <>
      <Modal
        srName="AddCommercialCriteriaModal"
        isOpen={isOpen}
        isClosable
        size={5}
        animate
        onClose={closeModal}
      >
        <AddCommercialCriteriaForm
          error={error}
          customerId={customerId}
          countryDetails={countryDetails}
          activeStep={activeStep}
          handleActiveStep={handleActiveStep}
          formValues={formValues}
          setFormValues={setFormValues}
          onSubmit={handleOnSubmit}
          closeModal={closeModal}
        />
      </Modal>
      <AddCommercialCriteriaSuccessModal
        isOpen={successModalModalOpen}
        body={<Span>{newCommercialCriteriaName} Added</Span>}
        heading={t(
          "core:commercialCriteria:add_commercial_criteria.added_successfully_modal_header"
        )}
        confirmText={t("core:accountConfig:okay")}
        onClose={() => {
          onDiscard();
        }}
      />
    </>
  );
};

export default AddCommercialCriteriaModal;
